import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MessageSquare } from "lucide-react";
import { adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { PageHeader } from "../components/layout";
import { StatusBadge } from "../components/status-badge";

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(value));
}

export function FunnelPage() {
  const { id } = useParams();
  const { token } = useAdminAuth();

  const funnel = useQuery({
    queryKey: ["admin", "funnel", id],
    queryFn: () => adminApi.funnel(token!, id!),
    enabled: Boolean(token && id),
  });

  const runs = useQuery({
    queryKey: ["admin", "funnel", id, "runs"],
    queryFn: () => adminApi.funnelRuns(token!, id!),
    enabled: Boolean(token && id),
  });

  if (funnel.isLoading || runs.isLoading) {
    return <div className="panel p-4 text-sm text-muted-foreground">Загружаем агента...</div>;
  }

  if (funnel.error || runs.error || !funnel.data) {
    return <div className="panel p-4 text-sm text-danger">{(funnel.error ?? runs.error)?.message}</div>;
  }

  return (
    <div>
      <PageHeader
        title={funnel.data.name}
        description={`Аккаунт ${funnel.data.accountId}, pipeline ${funnel.data.amoPipelineId}`}
        backTo={`/accounts/${funnel.data.accountId}`}
        backLabel="К агентам клиента"
      >
        <a
          className="inline-flex h-9 items-center gap-2 rounded-md border bg-white px-3 text-sm font-medium transition-colors hover:border-primary/40 hover:bg-accent"
          href="#agent-logs"
        >
          <MessageSquare className="h-4 w-4" />
          Логи
        </a>
      </PageHeader>

      <section className="panel mb-4 p-4">
        <div className="grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <p className="text-muted-foreground">Статус</p>
            <p className="font-medium">{funnel.data.active ? "активен" : "выключен"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Модель</p>
            <p className="font-medium">{funnel.data.model}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Разрешённые статусы</p>
            <p className="break-words font-medium">
              {funnel.data.amoStatusIds.length > 0 ? funnel.data.amoStatusIds.join(", ") : "все"}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-1 text-sm font-medium">Каналы</p>
          <div className="flex flex-wrap gap-2">
            {funnel.data.channels.length > 0 ? (
              funnel.data.channels.map((channel) => (
                <span key={channel.id} className="rounded-md border bg-muted px-2 py-1 text-xs">
                  {channel.type}: {channel.wazzupChannelId}
                </span>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">Каналы не подключены</span>
            )}
          </div>
        </div>

        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium">Системный промпт</summary>
          <div className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted p-3 text-sm">
            {funnel.data.systemPrompt}
          </div>
        </details>
      </section>

      <div id="agent-logs" className="mb-3 flex scroll-mt-20 items-center gap-2">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Последние запуски</h2>
      </div>

      <div className="space-y-3">
        {(runs.data ?? []).map((run) => (
          <Link
            key={run.id}
            className="panel block p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
            to={`/runs/${run.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={run.status} />
                  <span className="text-xs text-muted-foreground">{formatDate(run.createdAt)}</span>
                </div>
                <p className="mt-2 truncate text-sm font-medium">{run.inboundText}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  chatId {run.chatId}, событий {run.eventsCount ?? 0}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </div>
          </Link>
        ))}

        {runs.data?.length === 0 ? (
          <div className="panel p-4 text-sm text-muted-foreground">Запусков пока нет.</div>
        ) : null}
      </div>
    </div>
  );
}
